// Data audit — the global audit feed. Everything happening across Atlas:
// additions, changes and deletions from user edits, enrichment fills,
// DealCloud reconciles, integration syncs and agent runs. Reads
// GET /api/audit (+ /api/audit/facets for the filter dropdowns and the
// summary strip). The per-record "Change history" panel on a company/
// engagement reuses the same endpoint with object_type+object_id.

const _SOURCE_TONE = {
  ui: 'blue', firecrawl: 'teal', batch: 'teal', web_search: 'teal',
  dealcloud: 'violet', outlook: 'amber', fireflies: 'amber',
  agent: 'rose', system: 'slate',
};

const _actorIcon = (actorType) => {
  if (actorType === 'user') return <Icon.User size={13} />;
  if (actorType === 'integration') return <Icon.External size={13} />;
  if (actorType === 'agent') return <Icon.Bot size={13} />;
  return <Icon.Sparkle size={13} />; // system / enrichment
};

const _agoActivity = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!t) return '';
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return 'just now';
  const m = s / 60; if (m < 60) return `${Math.floor(m)}m ago`;
  const h = m / 60; if (h < 24) return `${Math.floor(h)}h ago`;
  const d = h / 24; if (d < 30) return `${Math.floor(d)}d ago`;
  return new Date(iso).toLocaleDateString();
};

const _trunc = (s, n) => {
  if (s == null) return '';
  s = String(s);
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
};

// firecrawl / legacy web_search / batch are all "web search" to the user —
// only the engine (and cost) differs. Collapse the badge label; the raw
// e.source is preserved in the data for cost analysis.
const _displaySource = (s) =>
  (s === 'firecrawl' || s === 'web_search' || s === 'batch') ? 'web search' : (s || '');

// One human sentence describing the event.
const _activityDetail = (e) => {
  const field = e.field ? e.field.replace(/_/g, ' ') : null;
  if (e.action === 'fill') {
    return e.new_value
      ? <>set <b>{field}</b> → <span className="mono">{_trunc(e.new_value, 60)}</span></>
      : <>looked for <b>{field}</b> — no match</>;
  }
  if (e.action === 'update') {
    return <>changed <b>{field}</b> {e.old_value ? <>from <span className="mono">{_trunc(e.old_value, 28)}</span> </> : ''}→ <span className="mono">{_trunc(e.new_value, 40)}</span></>;
  }
  // A create/delete that names a field is a sub-record of the object (an
  // industry alias) being added or removed, not the object itself — so
  // "added alias RIA", never "created industry".
  if (e.action === 'create') {
    return e.field
      ? <>added <b>{field}</b> <span className="mono">{_trunc(e.new_value, 40)}</span></>
      : <>created {e.object_type}</>;
  }
  if (e.action === 'delete') {
    return e.field
      ? <>removed <b>{field}</b> <span className="mono">{_trunc(e.old_value, 40)}</span></>
      : <>deleted {e.object_type}</>;
  }
  // Data-access rows (app/access_audit.py) carry a ready sentence; the
  // per-event details live in meta for the drawer.
  if (e.action === 'export' || e.action === 'download' || e.action === 'bulk_read' || e.action === 'mcp_call') {
    return e.summary || <>{e.action.replace(/_/g, ' ')} {e.object_label ? <span className="mono">{_trunc(e.object_label, 60)}</span> : e.object_type}</>;
  }
  return e.summary || <>{e.action}{field ? <> <b>{field}</b></> : ''}</>;
};

// Human labels for the summary strip. Any action outside this map still
// shows up in the Action dropdown under its raw name.
const _ACTION_STATS = [
  { action: 'create', label: 'Additions', hue: 'teal' },
  { action: 'update', label: 'Changes',   hue: 'blue' },
  { action: 'delete', label: 'Deletions', hue: 'rose' },
  { action: 'fill',   label: 'Fills',     hue: 'violet' },
  { action: 'export',    label: 'Exports',    hue: 'amber' },
  { action: 'bulk_read', label: 'Bulk pulls', hue: 'amber' },
  { action: 'download',  label: 'Downloads',  hue: 'slate' },
];

const _EMPTY_FILTERS = { object_type: '', action: '', source: '', actor: '', q: '', since: '', until: '' };

const _selStyle = {
  height: 26, fontSize: 12, padding: '0 6px', borderRadius: 6,
  border: '1px solid var(--border)', background: 'var(--surface)',
  color: 'var(--text-2)', maxWidth: 170,
};

const _facetLabel = (o) => (o.value === 'ui' ? 'user edit' : _displaySource(o.value)).replace(/_/g, ' ');

// Desktop filter row.
const FacetPicker = ({ value, onChange, options, placeholder }) => (
  <window.Picker value={value} onChange={onChange} emptyLabel={placeholder} placeholder={placeholder}
    size="sm" style={{ maxWidth: 170 }} menuMinWidth={220}
    options={options.map((o) => ({ value: o.value, label: _facetLabel(o), count: o.n }))} />
);

// Phone filter sheet: a native picker, so a pick doesn't open a second
// sheet over this one. `style` sizes it up for the sheet.
const FacetSelect = ({ value, onChange, options, placeholder, style }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} style={{
    ..._selStyle, ...style, color: value ? 'var(--text)' : 'var(--text-3)',
    fontWeight: value ? 600 : 400,
  }}>
    <option value="">{placeholder}</option>
    {options.map((o) => (
      <option key={o.value} value={o.value}>
        {_facetLabel(o)} ({o.n.toLocaleString()})
      </option>
    ))}
  </select>
);

// ── Phone ───────────────────────────────────────────────────────────────────
// On a phone (window.AtlasDevice.phone) the page keeps the same feed and
// filters, laid out for a thumb: search + a Filters button on top, the
// action counts as chips, the dropdowns and dates in a bottom sheet, and
// rows with bigger type and the full timestamp on show. Departure-watch
// Slack alerts link here, so it has to read well on a phone.
const _PHONE_TAP = { WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' };

// Sheet controls: 16px text (no iOS zoom on focus), 44px tall.
const _phoneControl = {
  width: '100%', maxWidth: 'none', height: 44, fontSize: 16, padding: '0 36px 0 12px', borderRadius: 10,
  border: '1px solid var(--border-strong)', background: 'var(--surface)', fontFamily: 'inherit',
  WebkitAppearance: 'none', appearance: 'none',
};

// Chip counts stay short: 1,234 · 12k · 1.2M.
const _fmtCount = (n) => {
  if (n == null) return null;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 10000) return `${Math.round(n / 1000)}k`;
  return n.toLocaleString();
};

// "Oct 6, 2026, 3:14 PM" — the absolute time a desktop only shows on hover.
const _phoneWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (!d.getTime()) return '';
  return d.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

// One event. A row about a company, deal or person opens that record.
const ActivityPhoneRow = ({ e, onOpen }) => {
  const tone = HUE[_SOURCE_TONE[e.source] || 'slate'] || HUE.slate;
  const clickable = e.object_id && ['company', 'engagement', 'contact'].includes(e.object_type);
  const Tag = clickable ? 'button' : 'div';
  // "3h ago · Oct 6, 2026, 3:14 PM". Past 30 days the relative part is
  // itself a date, so just the full time.
  const when = _phoneWhen(e.ts);
  const t = e.ts ? new Date(e.ts).getTime() : 0;
  const timeLine = !when ? '' : (t && Date.now() - t < 30 * 86400000 ? `${_agoActivity(e.ts)} · ${when}` : when);
  return (
    <Tag type={clickable ? 'button' : undefined} onClick={clickable ? () => onOpen(e) : undefined}
      style={{
        ..._PHONE_TAP, display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%', padding: '12px 16px',
        border: 'none', borderBottom: '1px solid var(--border)', background: 'var(--surface)', textAlign: 'left',
        color: 'var(--text)', fontFamily: 'inherit', cursor: clickable ? 'pointer' : 'default',
      }}>
      <span style={{ width: 28, height: 28, borderRadius: 8, background: tone.bg, color: tone.fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
        {_actorIcon(e.actor_type)}
      </span>
      <span style={{ display: 'block', flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 14.5, color: 'var(--text-2)', lineHeight: 1.45, overflowWrap: 'anywhere' }}>
          <span style={{ fontWeight: 600, color: 'var(--text)' }}>{e.object_label || e.object_type || 'Atlas'}</span>
          {' — '}
          {_activityDetail(e)}
        </span>
        <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 8px', marginTop: 5, fontSize: 13, color: 'var(--text-3)', overflowWrap: 'anywhere' }}>
          <Pill hue={_SOURCE_TONE[e.source] || 'slate'} sz="sm">{_displaySource(e.source).replace(/_/g, ' ')}</Pill>
          {e.object_type && <span>{e.object_type.replace(/_/g, ' ')}</span>}
          <span>{e.actor || e.actor_type}</span>
        </span>
        {timeLine && (
          <span className="num" style={{ display: 'block', marginTop: 3, fontSize: 13, color: 'var(--text-3)' }}>{timeLine}</span>
        )}
      </span>
      {clickable && <span style={{ flexShrink: 0, display: 'inline-flex', color: 'var(--text-4)', alignSelf: 'center' }}><Icon.ChevronR size={14} /></span>}
    </Tag>
  );
};

const ActivityView = ({ goToCompany, goToDeal, goToContact }) => {
  // useDevice re-renders on a phone ⇄ desktop switch. The phone-only state
  // below is declared on every render so the hook order never changes.
  const device = window.useDevice ? window.useDevice() : (window.AtlasDevice || {});
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const phoneScrollRef = React.useRef(null);
  const [filters, setFilters] = React.useState(_EMPTY_FILTERS);
  const [qInput, setQInput] = React.useState('');
  const [facets, setFacets] = React.useState(null);
  const [events, setEvents] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  // The last load's offset and whether it failed — only the phone layout
  // reads it (an error state, and dimming stale rows while a new filter
  // loads); desktop renders exactly as before.
  const [lastLoad, setLastLoad] = React.useState({ offset: 0, failed: false });
  const LIMIT = 100;

  // Debounce free-text search so we don't hit the API per keystroke.
  React.useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.q === qInput ? f : { ...f, q: qInput })), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  const activeParams = React.useCallback(() => {
    const p = {};
    for (const [k, v] of Object.entries(filters)) if (v) p[k] = v;
    return p;
  }, [filters]);

  const load = React.useCallback((offset) => {
    setLoading(true);
    setLastLoad({ offset, failed: false });
    window.AtlasApi.audit({ limit: LIMIT, offset, ...activeParams() }).then((res) => {
      const evs = (res && res.events) || [];
      setEvents((prev) => (offset > 0 ? [...prev, ...evs] : evs));
      setTotal((res && res.total) || 0);
      setLoading(false);
    }).catch(() => { setLastLoad({ offset, failed: true }); setLoading(false); });
  }, [activeParams]);

  React.useEffect(() => {
    load(0);
    window.AtlasApi.auditFacets(activeParams()).then(setFacets).catch(() => {});
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Phone: a new filter starts the list back at the top. (The ref is only
  // attached on a phone, so this is a no-op on desktop.)
  React.useEffect(() => {
    if (phoneScrollRef.current) phoneScrollRef.current.scrollTop = 0;
  }, [filters]);

  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
  const hasFilters = Object.values(filters).some(Boolean) || qInput;
  const actionCount = (a) => {
    const row = facets && facets.actions && facets.actions.find((x) => x.value === a);
    return row ? row.n : 0;
  };

  const openObject = (e) => {
    if (!e.object_id) return;
    if (e.object_type === 'company' && goToCompany) goToCompany(e.object_id);
    else if (e.object_type === 'engagement' && goToDeal) goToDeal(e.object_id);
    else if (e.object_type === 'contact' && goToContact) goToContact(e.object_id);
  };

  if (device.phone) {
    // The badge counts filters you can't see from the page: everything in
    // the sheet, except an action the chips already show as selected.
    const chipActions = _ACTION_STATS.map((s) => s.action);
    const hiddenCount = ['object_type', 'source', 'actor', 'since', 'until'].filter((k) => filters[k]).length
      + (filters.action && !chipActions.includes(filters.action) ? 1 : 0);
    const sheetActive = ['object_type', 'action', 'source', 'actor', 'since', 'until'].some((k) => filters[k]);
    const clearSheet = () => setFilters((f) => ({ ...f, object_type: '', action: '', source: '', actor: '', since: '', until: '' }));
    const clearAll = () => { setQInput(''); setFilters(_EMPTY_FILTERS); };
    const allCount = facets && facets.actions ? facets.actions.reduce((sum, a) => sum + (a.n || 0), 0) : null;
    const chipOptions = [
      { value: '', label: 'All', count: _fmtCount(allCount) },
      ..._ACTION_STATS.map((s) => ({ value: s.action, label: s.label, count: facets ? _fmtCount(actionCount(s.action)) : null })),
    ];
    const field = (label, control) => (
      <label style={{ display: 'block', flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 13, color: 'var(--text-3)', marginBottom: 6 }}>{label}</span>
        {control}
      </label>
    );
    const facetField = (label, key, placeholder, opts) => field(label, (
      <span style={{ position: 'relative', display: 'block' }}>
        <FacetSelect value={filters[key]} onChange={set(key)} placeholder={placeholder} options={opts || []} style={_phoneControl} />
        <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-3)', display: 'inline-flex' }}>
          <Icon.ChevronD size={14} />
        </span>
      </span>
    ));
    const dateField = (label, key) => field(label, (
      <input type="date" value={filters[key]} onChange={(e) => set(key)(e.target.value)}
        style={{ ..._phoneControl, padding: '0 12px', color: filters[key] ? 'var(--text)' : 'var(--text-3)' }} />
    ));
    const sheetBtn = { ..._PHONE_TAP, height: 46, borderRadius: 12, fontSize: 16, fontFamily: 'inherit', cursor: 'pointer' };
    const firstLoad = loading && events.length === 0;
    // A new filter's rows are still on the way: the old ones stay, dimmed.
    const refreshing = loading && lastLoad.offset === 0 && events.length > 0;
    // A failed load says so — not "No activity yet" over an empty or stale list.
    const failedList = !loading && lastLoad.failed && lastLoad.offset === 0;
    const failedMore = !loading && lastLoad.failed && lastLoad.offset > 0;
    const showRows = events.length > 0 && !failedList;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg)' }}>
        {/* Search, Filters and the action chips stay put while the feed scrolls. */}
        <div style={{ flexShrink: 0, background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '10px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 16px' }}>
            <window.PhoneSearchField value={qInput} onChange={setQInput} placeholder="Search events" style={{ flex: 1, minWidth: 0 }} />
            <button type="button" onClick={() => setFiltersOpen(true)}
              aria-label={hiddenCount ? `Filters, ${hiddenCount} on` : 'Filters'}
              style={{
                ..._PHONE_TAP, height: 40, padding: '0 12px', flexShrink: 0, borderRadius: 10, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 15, fontWeight: 500, fontFamily: 'inherit',
                border: '1px solid ' + (hiddenCount ? 'var(--accent)' : 'var(--border)'),
                background: hiddenCount ? 'var(--accent-soft)' : 'var(--surface)',
                color: hiddenCount ? 'var(--accent-text)' : 'var(--text-2)',
              }}>
              <Icon.Filter size={16} />
              Filters
              {hiddenCount > 0 && (
                <span className="num" style={{
                  minWidth: 20, height: 20, padding: '0 6px', borderRadius: 10, background: 'var(--accent)', color: '#fff',
                  fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}>{hiddenCount}</span>
              )}
            </button>
          </div>
          {/* Same as the desktop strip: tap an action to focus it, tap it again (or All) to let go. */}
          <window.PhoneChips value={filters.action} options={chipOptions} style={{ marginTop: 10 }}
            onChange={(v) => set('action')(v && filters.action === v ? '' : v)} />
        </div>

        <div style={{ flex: 1, minHeight: 0 }}>
          <window.PhoneScroll ref={phoneScrollRef}>
            {!firstLoad && showRows && (
              <div className="num" style={{ padding: '10px 16px 6px', fontSize: 13, color: 'var(--text-3)' }}>
                {refreshing ? 'Loading…' : `${total.toLocaleString()} ${total === 1 ? 'event' : 'events'}`}
              </div>
            )}

            {firstLoad && window.SkeletonRows && <window.SkeletonRows rows={8} variant="generic" />}

            {failedList && (
              <window.PhoneEmpty title="Couldn't load events" body="Check your connection and try again."
                action={<Btn size="md" variant="outline" onClick={() => load(0)}>Try again</Btn>} />
            )}

            {!loading && !failedList && events.length === 0 && (
              <window.PhoneEmpty
                title={hasFilters ? 'Nothing matches these filters' : 'No activity yet'}
                body={hasFilters ? 'Try another search or fewer filters.' : null}
                action={hasFilters ? <Btn size="md" variant="outline" onClick={clearAll}>Clear filters</Btn> : null} />
            )}

            {showRows && (
              <div style={{ borderTop: '1px solid var(--border)', opacity: refreshing ? 0.5 : 1, transition: 'opacity 120ms' }}>
                {events.map((e) => <ActivityPhoneRow key={e.id} e={e} onOpen={openObject} />)}
              </div>
            )}

            {showRows && !refreshing && events.length < total && (
              <div style={{ padding: '14px 16px' }}>
                <button type="button" onClick={() => load(events.length)} disabled={loading}
                  style={{
                    ..._PHONE_TAP, width: '100%', height: 48, borderRadius: 12, fontFamily: 'inherit',
                    border: '1px solid var(--border)', background: 'var(--surface)', fontSize: 16, fontWeight: 600,
                    color: loading ? 'var(--text-3)' : 'var(--text)', cursor: loading ? 'default' : 'pointer',
                  }}>
                  {loading ? 'Loading…' : failedMore ? 'Couldn’t load more. Try again' : `Load more (${(total - events.length).toLocaleString()} more)`}
                </button>
              </div>
            )}
          </window.PhoneScroll>
        </div>

        <window.PhoneSheet open={filtersOpen} onClose={() => setFiltersOpen(false)} title="Filters"
          footer={(
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" onClick={clearSheet} disabled={!sheetActive}
                style={{
                  ...sheetBtn, padding: '0 20px', border: '1px solid var(--border)', background: 'var(--surface)',
                  color: sheetActive ? 'var(--text)' : 'var(--text-4)', fontWeight: 500, cursor: sheetActive ? 'pointer' : 'default',
                }}>Clear</button>
              <button type="button" onClick={() => setFiltersOpen(false)}
                style={{ ...sheetBtn, flex: 1, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 600 }}>
                {loading ? 'Loading…' : failedList ? 'Done' : `Show ${total.toLocaleString()} ${total === 1 ? 'event' : 'events'}`}
              </button>
            </div>
          )}>
          <div style={{ padding: '4px 18px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {facetField('Type', 'object_type', 'All types', facets && facets.object_types)}
            {facetField('Action', 'action', 'All actions', facets && facets.actions)}
            {facetField('Source', 'source', 'All sources', facets && facets.sources)}
            {facetField('Who', 'actor', 'Everyone', facets && facets.actors)}
            <div style={{ display: 'flex', gap: 10 }}>
              {dateField('From', 'since')}
              {dateField('To', 'until')}
            </div>
          </div>
        </window.PhoneSheet>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        breadcrumb={['Workspace', 'Data audit']}
        title="Data audit"
      />

      {/* summary strip — counts under the current filter; click to focus an action */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderBottom: '1px solid var(--border)', background: 'var(--surface)', flexWrap: 'wrap' }}>
        {_ACTION_STATS.map((s) => {
          const active = filters.action === s.action;
          const tone = HUE[s.hue] || HUE.slate;
          return (
            <button key={s.action} onClick={() => set('action')(active ? '' : s.action)} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, height: 26,
              padding: '0 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
              border: '1px solid ' + (active ? tone.fg : 'var(--border)'),
              background: active ? tone.bg : 'transparent', color: 'var(--text-2)',
            }}>
              <span style={{ width: 8, height: 8, borderRadius: 4, background: tone.fg }} />
              {s.label}
              <span className="num" style={{ fontWeight: 600, color: 'var(--text)' }}>{actionCount(s.action).toLocaleString()}</span>
            </button>
          );
        })}
        <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--text-3)' }} className="num">
          {total.toLocaleString()} events
        </span>
      </div>

      {/* filter toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderBottom: '1px solid var(--border)', background: 'var(--surface)', flexWrap: 'wrap' }}>
        <input
          value={qInput} onChange={(e) => setQInput(e.target.value)}
          placeholder="Search label, field, value…"
          style={{ ..._selStyle, width: 200, maxWidth: 220, padding: '0 8px' }}
        />
        <FacetPicker value={filters.object_type} onChange={set('object_type')} placeholder="All types" options={(facets && facets.object_types) || []} />
        <FacetPicker value={filters.action} onChange={set('action')} placeholder="All actions" options={(facets && facets.actions) || []} />
        <FacetPicker value={filters.source} onChange={set('source')} placeholder="All sources" options={(facets && facets.sources) || []} />
        <FacetPicker value={filters.actor} onChange={set('actor')} placeholder="All actors" options={(facets && facets.actors) || []} />
        <input type="date" value={filters.since} onChange={(e) => set('since')(e.target.value)} title="From" style={_selStyle} />
        <span style={{ color: 'var(--text-3)', fontSize: 11 }}>–</span>
        <input type="date" value={filters.until} onChange={(e) => set('until')(e.target.value)} title="To" style={_selStyle} />
        {hasFilters && (
          <button onClick={() => { setQInput(''); setFilters(_EMPTY_FILTERS); }} style={{
            height: 26, padding: '0 8px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
            border: 'none', background: 'transparent', color: 'var(--text-3)', textDecoration: 'underline',
          }}>Clear</button>
        )}
      </div>

      <div style={{ flex: 1, overflow: 'auto' }}>
        {events.length === 0 && !loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No activity yet.</div>
        ) : (
          <div>
            {events.map((e) => {
              const tone = HUE[_SOURCE_TONE[e.source] || 'slate'] || HUE.slate;
              const clickable = e.object_id && ['company', 'engagement', 'contact'].includes(e.object_type);
              return (
                <div key={e.id} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 11, padding: '10px 16px',
                  borderBottom: '1px solid var(--border)',
                }}>
                  <span style={{ width: 26, height: 26, borderRadius: 7, background: tone.bg, color: tone.fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                    {_actorIcon(e.actor_type)}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5 }}>
                      {clickable ? (
                        <a onClick={() => openObject(e)} style={{ fontWeight: 600, color: 'var(--text)', cursor: 'pointer' }}>{e.object_label || e.object_type}</a>
                      ) : (
                        <span style={{ fontWeight: 600, color: 'var(--text)' }}>{e.object_label || e.object_type || 'Atlas'}</span>
                      )}
                      {' — '}
                      {_activityDetail(e)}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3, fontSize: 11, color: 'var(--text-3)' }}>
                      <Pill hue={_SOURCE_TONE[e.source] || 'slate'} sz="sm">{_displaySource(e.source).replace(/_/g, ' ')}</Pill>
                      {e.object_type && <span>{e.object_type.replace(/_/g, ' ')}</span>}
                      <span>{e.actor || e.actor_type}</span>
                    </div>
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--text-3)', whiteSpace: 'nowrap', flexShrink: 0 }} className="num" title={e.ts ? new Date(e.ts).toLocaleString() : ''}>{_agoActivity(e.ts)}</span>
                </div>
              );
            })}
            {events.length < total && (
              <div style={{ padding: 16, textAlign: 'center' }}>
                <Btn size="sm" variant="outline" onClick={() => load(events.length)} disabled={loading}>
                  {loading ? 'Loading…' : `Load more (${(total - events.length).toLocaleString()} more)`}
                </Btn>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

window.ActivityView = ActivityView;


// "View all" → the Data audit page, for the viewers who can browse it (the
// global feed needs view_audit_log; anyone else would land on an empty
// page). Mounted only inside an open timeline, so the permission lookup
// (cached) waits until someone looks.
const AuditViewAllLink = () => {
  const [allowed, setAllowed] = React.useState(false);
  React.useEffect(() => {
    let alive = true;
    if (window.AtlasCanSee && !window.AtlasCanSee('activity')) return undefined;
    if (!window.AtlasApi.authPermissions) return undefined;
    window.AtlasApi.authPermissions().then((res) => {
      const p = ((res && res.permissions) || []).find((x) => x.key === 'view_audit_log');
      if (alive) setAllowed(!!(p && p.allowed));
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!allowed) return null;
  return (
    <a href="#/activity" style={{ fontSize: 11.5, color: 'var(--accent-text)', textDecoration: 'none', whiteSpace: 'nowrap' }}
      onMouseOver={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
      onMouseOut={(e) => { e.currentTarget.style.textDecoration = 'none'; }}>View all</a>
  );
};

// Compact, self-contained audit panel for a single record (company/engagement
// detail pages). Reuses the feed's helpers; shows that record's change history
// — fills, user edits, stage moves — newest first, a page at a time ("Load
// older" pages on with offset). With `sectionId` it renders as a collapsible
// window.Section remembered under that id — folded by default, previewing
// "N changes · last <date>"; without one, the plain card it always was.
const AUDIT_PAGE = 50;
const AuditTimeline = ({ params, title = 'Change history', sectionId = null, defaultOpen = false }) => {
  const [events, setEvents] = React.useState(null);
  const [total, setTotal] = React.useState(0);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [moreFailed, setMoreFailed] = React.useState(false);
  const paramsKey = JSON.stringify(params);
  // The record this timeline is on now — a "Load older" answer for the
  // previous record is dropped.
  const keyRef = React.useRef(paramsKey);
  keyRef.current = paramsKey;

  React.useEffect(() => {
    let alive = true;
    setEvents(null); setTotal(0); setLoadingMore(false); setMoreFailed(false);
    window.AtlasApi.audit({ limit: AUDIT_PAGE, ...params }).then((res) => {
      if (!alive) return;
      setEvents((res && res.events) || []);
      setTotal((res && res.total) || 0);
    }).catch(() => { if (alive) setEvents([]); });
    return () => { alive = false; };
  }, [paramsKey]);   // eslint-disable-line react-hooks/exhaustive-deps

  const loadOlder = () => {
    if (loadingMore || !events) return;
    const key = paramsKey;
    setLoadingMore(true); setMoreFailed(false);
    window.AtlasApi.audit({ limit: AUDIT_PAGE, offset: events.length, ...params }).then((res) => {
      if (keyRef.current !== key) return;
      // Changes logged since the first page shift the offset — skip repeats.
      setEvents((prev) => {
        const seen = new Set((prev || []).map((e) => e.id));
        return [...(prev || []), ...((res && res.events) || []).filter((e) => !seen.has(e.id))];
      });
      if (res && res.total != null) setTotal(res.total);
      setLoadingMore(false);
    }).catch(() => {
      if (keyRef.current !== key) return;
      setMoreFailed(true); setLoadingMore(false);
    });
  };

  const last = events && events[0] && events[0].ts
    ? new Date(events[0].ts).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : null;
  const preview = events === null ? null
    : total === 0 ? 'No tracked changes yet'
    : `${total.toLocaleString()} change${total === 1 ? '' : 's'}${last ? ` · last ${last}` : ''}`;
  const hasOlder = !!events && events.length > 0 && events.length < total;

  const body = (
    <div style={{ maxHeight: 360, overflow: 'auto' }}>
      {events === null && <div style={{ padding: 14, fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>}
      {events && events.length === 0 && <div style={{ padding: 14, fontSize: 12, color: 'var(--text-3)' }}>No tracked changes yet.</div>}
      {events && events.map((e) => {
        const tone = HUE[_SOURCE_TONE[e.source] || 'slate'] || HUE.slate;
        return (
          <div key={e.id} style={{ display: 'flex', gap: 10, padding: '9px 14px', borderTop: '1px solid var(--border)' }}>
            <span style={{ width: 22, height: 22, borderRadius: 6, background: tone.bg, color: tone.fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{_actorIcon(e.actor_type)}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5 }}>{_activityDetail(e)}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 2, fontSize: 10.5, color: 'var(--text-3)' }}>
                <Pill hue={_SOURCE_TONE[e.source] || 'slate'} sz="sm">{_displaySource(e.source)}</Pill>
                <span>{e.actor || e.actor_type}</span>
              </div>
            </div>
            <span style={{ fontSize: 10.5, color: 'var(--text-3)', whiteSpace: 'nowrap', flexShrink: 0 }} className="num">{_agoActivity(e.ts)}</span>
          </div>
        );
      })}
      {events && events.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', borderTop: '1px solid var(--border)' }}>
          {hasOlder && (
            <Btn size="xs" variant="outline" onClick={loadOlder} disabled={loadingMore}>
              {loadingMore ? 'Loading…' : moreFailed ? 'Couldn’t load — try again' : `Load older · ${(total - events.length).toLocaleString()}`}
            </Btn>
          )}
          <span style={{ flex: 1 }} />
          <AuditViewAllLink />
        </div>
      )}
    </div>
  );

  if (sectionId && window.Section) {
    return (
      <window.Section title={`${title}${total > 0 ? ` · ${total.toLocaleString()}` : ''}`}
        collapsible id={sectionId} defaultOpen={defaultOpen} preview={preview}>
        {body}
      </window.Section>
    );
  }
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface)', overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{title}</span>
        {total > 0 && <span style={{ fontSize: 11, color: 'var(--text-3)' }} className="num">{total}</span>}
      </div>
      {body}
    </div>
  );
};

window.AuditTimeline = AuditTimeline;

// ── History drawer body (record-page redesign step 10; ?panel=history&tab=changes|stages|buyer|sync).
// Tabs from the registry's HISTORY_TABS per kind, filtered per viewer: changes (not guests),
// stages (deals), buyer (roster firms), sync (DC-linked, not guests). Changes = AuditTimeline on
// People (user + integration = the DealCloud sync, labelled Sync by the source pill); the
// System chip adds automation. Any member may read one record's history, so the chip's
// count comes from two one-row reads (audit/facets needs view_audit_log).
// The audit object_type per kind is RecordPanelsCore.OBJECT_TYPE (read at render; no copy here).
const HISTORY_PEOPLE = 'user,integration';
const HISTORY_LABEL = { changes: 'Changes', stages: 'Stage moves', buyer: 'Buyer profile', sync: 'Sync' };
// The per-kind tab list is the registry's (record_panels.jsx anchor A exports it); read at call time.
const historyTabsFor = (kind, { guest, dealcloudId, buyerFirm }) => {
  const all = (window.RECORD_HISTORY_TABS || {})[kind] || [];
  return all.filter((t) => (t === 'changes' ? !guest : t === 'sync' ? !guest && !!dealcloudId : t === 'buyer' ? !!buyerFirm : true));
};
const _histTab = { border: 'none', borderRadius: 7, padding: '5px 10px', cursor: 'pointer', fontSize: 12.5, fontFamily: 'inherit' };
const RecordHistoryBody = ({ api }) => {
  const kind = api.rec.kind; const id = api.rec.id;
  const objectType = window.RecordPanelsCore.OBJECT_TYPE[kind];
  const rec = api.record || {}; const raw = rec._raw || {}; const ctx = api.ctx || {};
  const dealcloudId = rec.dealcloudId || raw.dealcloud_id || null;
  // A roster firm's buyer id: the loaded buyer row, or the id step 12 puts on the ctx before the row arrives (A4).
  const buyerId = (ctx.buyer && ctx.buyer.buyer_id) || ctx.buyerId || null;
  const tabs = historyTabsFor(kind, { guest: api.guest, dealcloudId, buyerFirm: !!(api.facts && api.facts.buyerFirm) && !!buyerId });
  const tab = tabs.includes(api.params.tab) ? api.params.tab : (tabs[0] || null);
  const [system, setSystem] = React.useState(false);
  const [counts, setCounts] = React.useState(null);            // { all, people } for the chip
  const [stages, setStages] = React.useState(ctx.stageHistory === undefined ? null : ctx.stageHistory);
  React.useEffect(() => {                                       // the chip's N: all − people
    if (api.guest || tab !== 'changes' || !api.active) return undefined;
    let alive = true;
    const base = { object_type: objectType, object_id: id, limit: 1 };
    Promise.all([window.AtlasApi.audit(base), window.AtlasApi.audit({ ...base, actor_type: HISTORY_PEOPLE })])
      .then(([a, p]) => { if (alive) setCounts({ all: (a && a.total) || 0, people: (p && p.total) || 0 }); })
      .catch(() => { if (alive) setCounts(null); });
    return () => { alive = false; };
  }, [kind, id, tab, api.active, api.guest]);
  React.useEffect(() => {                                       // Stage moves: DealDetail's rows when it has them
    if (kind !== 'deal' || tab !== 'stages' || !api.active) return undefined;
    if (Array.isArray(ctx.stageHistory)) { setStages(ctx.stageHistory); return undefined; }
    let alive = true;
    window.AtlasApi.listStageHistory(id).then((rows) => { if (alive) setStages(Array.isArray(rows) ? rows : []); }).catch(() => { if (alive) setStages([]); });
    return () => { alive = false; };
  }, [kind, id, tab, api.active, ctx.stageHistory]);
  if (!tab) return <div style={{ padding: 14, color: 'var(--text-4)' }}>—</div>;
  const n = counts ? Math.max(0, counts.all - counts.people) : null;
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : null);
  return (
    <div data-history-body={tab}>
      <div role="tablist" style={{ display: 'flex', gap: 4, marginBottom: 10, flexWrap: 'wrap' }}>
        {tabs.map((t) => (
          <button key={t} type="button" role="tab" aria-selected={t === tab} onClick={() => api.set({ tab: t })}
            style={{ ..._histTab, fontWeight: t === tab ? 600 : 500, background: t === tab ? 'var(--accent-soft)' : 'transparent', color: t === tab ? 'var(--accent-text)' : 'var(--text-2)' }}>{HISTORY_LABEL[t]}</button>
        ))}
        {tab === 'changes' && (
          <button type="button" aria-pressed={system} onClick={() => setSystem((x) => !x)}
            style={{ ..._histTab, marginLeft: 'auto', border: '1px solid var(--border)', background: system ? 'var(--accent-soft)' : 'var(--surface)', color: system ? 'var(--accent-text)' : 'var(--text-3)', fontSize: 11.5 }}>
            {`System · ${n == null ? '—' : n}`}
          </button>
        )}
      </div>
      {tab === 'changes' && (
        <div style={{ margin: '0 -14px' }}>
          <window.AuditTimeline params={{ object_type: objectType, object_id: id, actor_type: system ? undefined : HISTORY_PEOPLE }} />
        </div>
      )}
      {tab === 'stages' && (
        stages == null ? <div style={{ padding: 14, color: 'var(--text-4)' }}>—</div>
        : stages.length === 0 ? <div style={{ padding: 14, color: 'var(--text-4)' }}>0</div>
        : stages.map((h, i) => (
          <div key={h.id || i} style={{ display: 'flex', gap: 10, padding: '8px 0', borderTop: i === 0 ? 'none' : '1px solid var(--border)', alignItems: 'center', fontSize: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ color: 'var(--text-3)' }}>{h.from_stage_name || h.from_stage_slug || '(new)'}</span>
              <span style={{ margin: '0 6px', color: 'var(--text-4)' }}>→</span>
              <span style={{ color: 'var(--text)', fontWeight: 500 }}>{h.to_stage_name || h.to_stage_slug}</span>
              {h.exit_name && <span style={{ marginLeft: 6 }}><Pill hue="rose" sz="sm">{h.exit_name}</Pill></span>}
              {h.changed_by_name && <span style={{ marginLeft: 6, color: 'var(--text-3)' }}>{`· ${h.changed_by_name}`}</span>}
            </div>
            <span className="num" style={{ fontSize: 11, color: 'var(--text-3)', flexShrink: 0 }}>{new Date(h.created_at).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}</span>
          </div>))
      )}
      {tab === 'buyer' && buyerId && (
        <div style={{ margin: '0 -14px' }}><window.AuditTimeline params={{ object_type: 'buyer', object_id: buyerId }} /></div>
      )}
      {tab === 'sync' && (
        <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>
          {'Last synced '}{fmt(rec.lastSyncedAt || raw.last_synced_at) || <span style={{ color: 'var(--text-4)' }}>—</span>}
          <span style={{ color: 'var(--text-4)' }}> · </span>{'edited here '}{fmt(rec.updatedAt || raw.updated_at) || <span style={{ color: 'var(--text-4)' }}>—</span>}
        </div>
      )}
    </div>
  );
};
Object.assign(window, { RecordHistoryBody, historyTabsFor });
